import Head from "next/head";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import SharedSnackbar from "../../components/shared/SharedSnackbar/SharedSnackbar";
import * as config from "../../config/config";
import useTriggerSnackbar from "../../hooks/useTriggerSnackbar";
import { fetchRetrenchementPageContent } from "../../redux/actions/company_action";
import { sendVisitorLeadEnquiryForm } from "../../redux/actions/user_action";
import retrenchmentSupportGuideStyles from "../../styles/retrenchment-support-guide";

const listingFields = `
  id
  listItem
`;

const flipCardFields = `
  id
  partnerName
  partnerTagText
  partnerHeadline
  partnerSubheadline
  partnerBenefits {
    ${listingFields}
  }
  partnerLogo {
    data {
      attributes {
        url
        alternativeText
      }
    }
  }
  partnerBackTitle
  partnerBackDescription
  partnerBackList {
    ${listingFields}
  }
  ctaLink
  ctaText
`;

const linkCardFields = `
  id
  cardTitle
  cardDescription
  cardTag
  ctaLink
  ctaText
`;

const articleRelationFields = `
  data {
    id
    attributes {
      title
      slug
      excerpt
      redirectionLink
      articleCategories {
        data {
          attributes {
            category
          }
        }
      }
      articleSubcategories {
        data {
          attributes {
            subcategory
          }
        }
      }
    }
  }
`;

const retrenchmentPageQuery = `
  partnerListTopSection{
    sectionTitle
    sectionDescription
  }
  partnerListMiddleSection{
    sectionTitle
    sectionDescription
  }
  partnerListBottomSection{
    sectionTitle
    sectionDescription
  }
  resourcesLinkCards{
    ${linkCardFields}
  }
  partnerListTop {
    ${flipCardFields}
  }
  partnerListBottom {
    ${flipCardFields}
  }
  videoLinks {
    ${listingFields}
  }
  articles {
    ${articleRelationFields}
  }
  upskillSection{
    tagline
    sectionTitle
    sectionDescription
    sectionSubtitle
    sectionSubDescription
    upskillLinkCards{
      ${linkCardFields}
    }
  }
`;

const metaTitle =
  "Retrenchment Support Guide Malaysia: What to Do After Job Loss | Hiredly";
const metaDescription =
  "A practical guide for Malaysians who've been retrenched or suffered job loss. Claim your SOCSO EIS, build a recruiter-ready resume, and get hired faster on Hiredly. Navigating Job Loss in Malaysia - Your Step-by-Step Retrenchment Support Guide";

const landingAsset = (filename) =>
  config.assetDomain + "/images/retrenchment_landing/" + filename;

const getStrapiMediaUrl = (media) => media?.data?.attributes?.url || "";

const PARTNER_TAG_BY_TEXT = {
  free: { className: "tag-free", label: "Free" },
  partner: { className: "tag-partner", label: "Partner" },
  support: { className: "tag-partner", label: "Support" },
};

function getPartnerTag(partnerTagText) {
  if (!partnerTagText || partnerTagText === "none") return null;
  return PARTNER_TAG_BY_TEXT[String(partnerTagText).toLowerCase()] ?? null;
}

function isPhoneNumber(text) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("tel:")) return true;
  const digitsOnly = trimmed.replace(/[\s\-().+]/g, "");
  return digitsOnly.length >= 3 && /^\+?\d+$/.test(digitsOnly);
}

function phoneToTelHref(phone) {
  const trimmed = String(phone).trim();
  if (trimmed.startsWith("tel:")) return trimmed;
  const digits = trimmed.replace(/[^\d+]/g, "").replace(/^\+/, "");
  return `tel:${digits}`;
}

function PartnerBenefits({ benefits }) {
  if (!benefits?.length) return null;

  if (benefits.length === 1 && isPhoneNumber(benefits[0].listItem)) {
    const phone = benefits[0].listItem.trim();
    return (
      <a href={phoneToTelHref(phone)} className="content-link partner-contact">
        {phone}
      </a>
    );
  }

  return (
    <div className="partner-benefits">
      {benefits.map((benefit) => (
        <span key={benefit.id} className="partner-benefit">
          {benefit.listItem}
        </span>
      ))}
    </div>
  );
}

function ResourceLinkCard({ card }) {
  const cardTag = getPartnerTag(card.cardTag);

  return (
    <div className="partner-card">
      <div className="partner-info">
        <h4>
          {card.cardTitle}
          {cardTag ? (
            <span className={`partner-tag ${cardTag.className}`}>
              {cardTag.label}
            </span>
          ) : null}
        </h4>
        {card.cardDescription ? <p>{card.cardDescription}</p> : null}
      </div>
      {card.ctaText ? (
        <a
          href={card.ctaLink || "#"}
          className="partner-link partner-link--simple"
          {...(card.ctaLink
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
        >
          {card.ctaText}
        </a>
      ) : null}
    </div>
  );
}

function PartnerFlipCard({ partner }) {
  const logoUrl = getStrapiMediaUrl(partner.partnerLogo);
  const logoAlt =
    partner.partnerLogo?.data?.attributes?.alternativeText ||
    partner.partnerName ||
    "Partner logo";
  const partnerTag = getPartnerTag(partner.partnerTagText);

  return (
    <div className="partner-card partner-card--bridge">
      <div className="partner-flip">
        <div className="partner-face partner-face--front">
          <div className="partner-logo" aria-hidden="true">
            {logoUrl ? <img src={logoUrl} alt={logoAlt} /> : null}
          </div>
          <div className="partner-content">
            <div className="partner-info">
              <h4>
                {partner.partnerName}
                {partnerTag ? (
                  <span className={`partner-tag ${partnerTag.className}`}>
                    {partnerTag.label}
                  </span>
                ) : null}
              </h4>
            </div>
            {partner.partnerHeadline ? (
              <div className="partner-headline">{partner.partnerHeadline}</div>
            ) : null}
            {partner.partnerSubheadline ? (
              <div className="partner-subheadline">
                {partner.partnerSubheadline}
              </div>
            ) : null}
            <PartnerBenefits benefits={partner.partnerBenefits} />
          </div>
        </div>
        <div className="partner-face partner-face--back">
          <div className="partner-logo" aria-hidden="true">
            {logoUrl ? <img src={logoUrl} alt={logoAlt} /> : null}
          </div>
          <div className="partner-back-panel">
            <div>
              {partner.partnerBackTitle ? (
                <div className="partner-back-title">
                  {partner.partnerBackTitle}
                </div>
              ) : null}
              {partner.partnerBackDescription ? (
                <div className="partner-back-copy">
                  {partner.partnerBackDescription}
                </div>
              ) : null}
              {partner.partnerBackList?.length > 0 ? (
                <ul className="partner-back-list">
                  {partner.partnerBackList.map((item) => (
                    <li key={item.id}>{item.listItem}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <a
        href={partner.ctaLink || "#"}
        className="partner-cta-rail partner-link"
        {...(partner.ctaLink
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {partner.ctaText || "Sign up →"}
      </a>
    </div>
  );
}

function groupVideoLinks(links = []) {
  const groups = [];
  for (let i = 0; i < links.length; i += 2) {
    groups.push(links.slice(i, i + 2));
  }
  return groups;
}

function slugToTitle(slug) {
  if (!slug) return "";
  return decodeURIComponent(slug)
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function getArticleHref(article) {
  const attrs = article?.attributes;
  if (!attrs) return "#";
  if (attrs.redirectionLink) return attrs.redirectionLink;
  if (attrs.slug) return `/advice/${attrs.slug}`;
  return "#";
}

function getArticleTag(article) {
  const attrs = article?.attributes;
  const category = attrs?.articleCategories?.data?.[0]?.attributes?.category;
  const subcategory =
    attrs?.articleSubcategories?.data?.[0]?.attributes?.subcategory;
  return category || subcategory || "Article";
}

function RelatedReadingGrid({ articles }) {
  const items = articles?.data ?? [];
  if (!items.length) return null;

  return (
    <div className="reading-grid">
      {items.map((article) => {
        const attrs = article.attributes;
        return (
          <a
            key={article.id}
            className="reading-card"
            href={getArticleHref(article)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="reading-card-top">
              <div>
                <span className="reading-tag">{getArticleTag(article)}</span>
                <h3>{slugToTitle(attrs?.slug)}</h3>
                <span className="reading-card-kicker">
                  <span className="reading-card-kicker-text">Read more</span>
                </span>
              </div>
            </div>
            {attrs?.excerpt ? (
              <div className="reading-card-body">
                <p>{attrs.title}</p>
              </div>
            ) : null}
          </a>
        );
      })}
    </div>
  );
}

function InterviewVideoGrid({ videoLinks = [] }) {
  if (!videoLinks.length) return null;

  const rows = groupVideoLinks(videoLinks);

  return (
    <div className="video-grid" aria-label="Interview prep video examples">
      {rows.map((row, rowIndex) => {
        const isSingleRow =
          row.length === 1 && (rowIndex > 0 || videoLinks.length === 1);

        return (
          <div
            key={row.map((v) => v.id).join("-")}
            className={
              isSingleRow ? "video-row video-row--single" : "video-row"
            }
          >
            {row.map((video) => (
              <article
                key={video.id}
                className={
                  isSingleRow ? "video-card video-card--single" : "video-card"
                }
              >
                <div className="video-embed">
                  <iframe
                    src={video.listItem}
                    allow="encrypted-media; fullscreen; picture-in-picture"
                    allowFullScreen
                    title="Interview prep video"
                  />
                </div>
              </article>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function HiredlyRetrenchmentSupportGuide() {
  const [pageContent, setPageContent] = useState(null);
  const [pdfEmail, setPdfEmail] = useState("");
  const [pdfEmailSubmitting, setPdfEmailSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const triggerToast = (bool, message) => {
    triggerSnackbarFunc({
      snackbarMessage: message,
      severity: bool ? "success" : "error",
    });
  };

  const handlePdfEmailSubmit = async (e) => {
    e.preventDefault();
    if (pdfEmailSubmitting) return;

    setPdfEmailSubmitting(true);
    try {
      const result = await dispatch(
        sendVisitorLeadEnquiryForm({
          input: {
            email: pdfEmail.trim(),
            leadSource: "retrenchment_support",
            segment: "retrenchment_support",
          },
        })
      );

      if (result?.success) {
        triggerToast(
          true,
          "Submitted successfully! You may check your inbox in a while"
        );
        setPdfEmail("");
      } else {
        triggerToast(
          false,
          result?.error || "Please contact us for assistance"
        );
      }
    } catch (error) {
      triggerToast(false, "Please contact us for assistance");
    } finally {
      setPdfEmailSubmitting(false);
    }
  };

  async function fetchData() {
    const attributes = await dispatch(
      fetchRetrenchementPageContent(retrenchmentPageQuery)
    );

    setPageContent(attributes);
    // partnerListTop is an array of flip-card components
    // console.log(attributes?.partnerListTop);
  }

  useEffect(() => {
    fetchData();
    const root = document.querySelector(".retrenchment-support-guide");
    if (!root) return;

    const stickyBar = root.querySelector("#stickyBar");
    const hero = root.querySelector(".hero");
    const toc = root.querySelector("#toc");
    const darkTocSections = new Set();

    const scrollOffset = 120;

    const onScroll = () => {
      if (stickyBar && hero) {
        if (hero.getBoundingClientRect().bottom <= 0) {
          stickyBar.classList.add("visible");
        } else {
          stickyBar.classList.remove("visible");
        }
      }

      const tocLinks = root.querySelectorAll(".toc-link");
      const sections = root.querySelectorAll(
        "section[id], header[id], .partner-group[id]"
      );
      let current = "";
      const scrollY = window.scrollY;
      sections.forEach((sec) => {
        const top = scrollY + sec.getBoundingClientRect().top - scrollOffset;
        if (scrollY >= top) current = sec.id;
      });
      tocLinks.forEach((link) => {
        link.classList.toggle(
          "active",
          link.getAttribute("href") === "#" + current
        );
      });
      if (toc) {
        toc.classList.toggle("on-dark", darkTocSections.has(current));
      }
    };

    const faqHandlers = [];
    root.querySelectorAll(".faq-question").forEach((q) => {
      const handler = () => {
        q.parentElement.classList.toggle("open");
      };
      q.addEventListener("click", handler);
      faqHandlers.push({ q, handler });
    });

    root.querySelectorAll("a[href]").forEach((link) => {
      const href = link.getAttribute("href") || "";
      if (
        !href.startsWith("#") &&
        !href.startsWith("javascript:") &&
        !href.startsWith("tel:") &&
        !href.startsWith("mailto:")
      ) {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
      }
    });

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      faqHandlers.forEach(({ q, handler }) => {
        q.removeEventListener("click", handler);
      });
    };
  }, []);

  return (
    <Fragment>
      <Head>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        <meta property="og:type" content="website" key="ogType" />
        <meta
          property="og:url"
          content="https://my.hiredly.com/retrenchment-support-guide"
          key="ogUrl"
        />
        <meta property="og:title" content={metaTitle} key="ogTitle" />
        <meta
          property="og:description"
          content={metaDescription}
          key="ogDescription"
        />
        <meta
          property="og:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="ogImage"
        />

        <meta
          property="twitter:card"
          content="summary_large_image"
          key="twitterCard"
        />
        <meta
          property="twitter:url"
          content="https://my.hiredly.com/retrenchment-support-guide"
          key="twitterUrl"
        />
        <meta property="twitter:title" content={metaTitle} key="twitterTitle" />
        <meta
          property="twitter:description"
          content={metaDescription}
          key="twitterDescription"
        />
        <meta
          property="twitter:image"
          content={
            config.assetDomain + "/images/hiredly/hiredly-meta-image-v1.png"
          }
          key="twitterImage"
        />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <link
          rel="canonical"
          href="https://my.hiredly.com/retrenchment-support-guide"
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@500;600;700&display=swap"
          rel="stylesheet"
        />
        <style
          dangerouslySetInnerHTML={{ __html: retrenchmentSupportGuideStyles }}
        />
      </Head>

      <div className="retrenchment-support-guide">
        {/*Sticky CTA Bar*/}
        <div className="sticky-bar" id="stickyBar">
          <a href="/" className="brand" aria-label="Hiredly">
            <img
              src="/images/hiredly-logo.svg"
              alt="Hiredly"
              className="brand-logo"
            />
          </a>
          <a href="#signup" className="cta-small">
            Get Me Hired
          </a>
        </div>

        {/*Sidebar TOC (desktop only)*/}
        <nav className="toc" id="toc">
          <ul>
            <li>
              <a href="#first-72" className="toc-link">
                First 72 hours
              </a>
            </li>
            <li>
              <a href="#finances" className="toc-link">
                Stay financially stable
              </a>
            </li>
            <li>
              <a href="#hiredly" className="toc-link">
                Get hired faster
              </a>
            </li>
            <li>
              <a href="#partners" className="toc-link">
                Resources & partners
              </a>
              <ul className="toc-sub">
                <li>
                  <a href="#bridge-income" className="toc-link">
                    Bridge Income
                  </a>
                </li>
                <li>
                  <a href="#health-insurance" className="toc-link">
                    Health Insurance
                  </a>
                </li>
                <li>
                  <a href="#mental" className="toc-link">
                    Mental Health
                  </a>
                </li>
              </ul>
            </li>
            <li>
              <a href="#upskill" className="toc-link">
                Upskill
              </a>
            </li>
            <li>
              <a href="#scams" className="toc-link">
                Avoid scams
              </a>
            </li>
            <li>
              <a href="#interview" className="toc-link">
                Interview prep
              </a>
            </li>
            <li>
              <a href="#faq" className="toc-link">
                FAQ
              </a>
            </li>
          </ul>
        </nav>

        {/*HERO*/}
        <header className="hero">
          <div className="wrap">
            <h1>
              Navigating Job Loss in Malaysia:
              <br />
              Your Step-by-Step <br />
              <span className="accent">Retrenchment Support Guide</span>
            </h1>
            <p className="lead">
              Retrenchment in Malaysia has been climbing. This page is a
              practical 30-day guide for what to do next, from claiming your
              SOCSO EIS to landing your next job faster.
            </p>
            <div className="cta-row">
              <a
                href="https://my.hiredly.com/?signup=true"
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Create my Hiredly profile
              </a>
              <a href="#first-72" className="btn btn-secondary">
                Show me the guide ↓
              </a>
            </div>
            <span className="btn-note">
              Sign up through this page and get more visibility from employers
              so you get hired faster.
            </span>
          </div>
        </header>

        {/*FIRST 72 HOURS*/}
        <section id="first-72">
          <div className="wrap">
            <span className="eyebrow">Step 1 — Today & tomorrow</span>
            <h2>Your first 72 hours</h2>
            <p className="lead">
              Before you do anything else, do these. A few of them have hard
              deadlines you don't want to miss.
            </p>

            <div className="checklist">
              <div className="check-item">
                <div className="check-num">1</div>
                <div className="check-content">
                  <h3>
                    File your SOCSO EIS claim{" "}
                    <span className="deadline">
                      60-DAY DEADLINE: Apply via{" "}
                      <a
                        href="https://www.perkeso.gov.my/en/our-services/protection/employment-insurance.html"
                        className="content-link"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        <i>Perkeso</i>
                      </a>{" "}
                    </span>
                  </h3>
                  <p>
                    EIS can pay up to 6 months of income replacement. Payments
                    start at 80% of your salary for the first two months, reduce
                    to 50% for the next 2-3 months and drop to 30% for the final
                    months.
                  </p>
                </div>
              </div>

              <div className="check-item">
                <div className="check-num">2</div>
                <div className="check-content">
                  <h3>
                    Register on the{" "}
                    <a
                      href="https://myfuturejobs.gov.my/en/register"
                      className="content-link"
                      style={{
                        color: "inherit",
                        fontWeight: "bold",
                      }}
                    >
                      <i>MYFutureJobs Portal</i>
                    </a>
                  </h3>
                  <p>
                    This step is mandatory for EIS claims. After applying,
                    MYFutureJobs will match you with suitable vacancies. IPs can
                    also access skills training during unemployment or after
                    re-employment under the EIS program.
                  </p>
                </div>
              </div>

              <div className="check-item">
                <div className="check-num">3</div>
                <div className="check-content">
                  <h3>Review Your Retrenchment Package</h3>
                  <p>
                    Check your retrenchment letter for your exact termination
                    date, severance payout and payment for unused annual leave.
                    If the terms seem unfair, seek advice from the{" "}
                    <a
                      href="https://jtksm.mohr.gov.my/ms/laman-utama"
                      className="content-link"
                    >
                      JTKSM
                    </a>{" "}
                    before signing away your rights.
                  </p>
                </div>
              </div>

              <div className="check-item">
                <div className="check-num">4</div>
                <div className="check-content">
                  <h3>Get Your Termination Letter in Writing</h3>
                  <p>
                    Ask HR for a clear, written letter stating the exact reason
                    for termination (e.g., retrenchment, VSS, MSS) and request a
                    formal testimonial or letter of recommendation from your
                    manager or HR.
                  </p>
                </div>
              </div>

              <div className="check-item">
                <div className="check-num">5</div>
                <div className="check-content">
                  <h3>Update your CV & Create a Hiredly Profile</h3>

                  <p>
                    Use{" "}
                    <a href="https://hiresume.my" className="content-link">
                      HiResume.my
                    </a>{" "}
                    to build an ATS-friendly CV quickly. Then, create your{" "}
                    <a
                      href="https://my.hiredly.com/?signup=true"
                      className="content-link"
                    >
                      Hiredly
                    </a>{" "}
                    profile to start searching for active jobs immediately.
                  </p>
                </div>
              </div>
            </div>

            {/*Email capture*/}
            <div className="email-capture">
              <h3>Want this as a checklist you can save?</h3>
              <p>
                We'll email you a free PDF:{" "}
                <strong>
                  Hiredly's Retrenchment Support Guide — your 30-day checklist.
                </strong>{" "}
                No spam, just this, job recommendations and the occasional job
                tip.
              </p>
              <form className="email-form" onSubmit={handlePdfEmailSubmit}>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={pdfEmail}
                  onChange={(e) => setPdfEmail(e.target.value)}
                  required
                  disabled={pdfEmailSubmitting}
                />
                <button type="submit" disabled={pdfEmailSubmitting}>
                  {pdfEmailSubmitting ? "Sending..." : "Send me the PDF"}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/*FINANCIAL*/}
        <section id="finances">
          <div className="wrap">
            <span className="eyebrow">Step 2 — Breathing room</span>
            <h2>Stay financially stable</h2>
            <p className="lead">
              The shock of losing a job is financial before it's anything else.
              These are the levers most Malaysians don't know they can pull.
            </p>

            <div className="stat-row">
              <div className="stat">
                <div className="stat-value">80%</div>
                <div className="stat-label">
                  of your last salary in your first EIS month
                </div>
              </div>
              <div className="stat">
                <div className="stat-value stat-value--duration">
                  3 to 6 <span className="stat-unit">months</span>
                </div>
                <div className="stat-label">
                  of EIS income support, delivered in staggered payouts.
                </div>
              </div>
              <div className="stat">
                <div className="stat-value stat-value--deadline">
                  60 <span className="stat-unit">days</span>
                </div>
                <div className="stat-label">to file your EIS claim</div>
              </div>
            </div>

            <h3 style={{ marginTop: "2rem" }}>EIS benefit, explained simply</h3>
            <p>
              Under{" "}
              <a
                href="https://www.perkeso.gov.my/en/our-services/protection/employment-insurance.html"
                className="content-link"
              >
                PERKESO’s Employment Insurance System (EIS)
              </a>
              , if you earned RM5,000/month, your first month of EIS pays
              roughly RM4,000 (80%), with the amount gradually stepping down in
              the following months. It’s the safety net you need while you focus
              on landing your next big role.
            </p>

            <h3 style={{ marginTop: "1.5rem" }}>
              KWSP (EPF) Financial Support
            </h3>
            <p>
              For retrenched employees, KWSP (EPF) allows immediate access to
              retirement savings via the{" "}
              <a
                href="https://www.kwsp.gov.my/en/member/life-stages/akaun-fleksibel-withdrawal"
                className="content-link"
              >
                Akaun Fleksibel (Account 3)
              </a>{" "}
              for quick cash on hand, or other pre-retirement withdrawals, and
              exempts retrenchment benefits from statutory contributions.
              <br />
              <br />
              <strong>
                Key EPF Support & Provisions for Retrenchment (2026):
              </strong>
              <br />
              <strong>Akaun Fleksibel (Account 3) Withdrawal:</strong> As of
              April 2024, members under 55 can withdraw savings from this
              account at any time for financial needs, with a minimum withdrawal
              of RM50.
            </p>

            <h3 style={{ marginTop: "1.5rem" }}>
              Other levers worth knowing about
            </h3>
            <p>
              <strong>Postpone your PTPTN payments.</strong> If you have a study
              loan, you can apply to delay your monthly installments while
              you’re out of work.{" "}
              <a
                href="https://www.ptptn.gov.my/en/bayaran-balik/"
                className="content-link"
              >
                PTPTN's portal
              </a>{" "}
              handles these applications online.
            </p>

            <p>
              <strong>Contact Creditors Immediately.</strong> Do not wait for
              missed payments. Contact your bank and credit card issuers to
              explain your retrenchment. Many financial institutions offer
              temporary repayment assistance
            </p>

            <p>
              <strong>AKPK — free debt counselling.</strong>{" "}
              <a href="https://www.akpk.org.my" className="content-link">
                Agensi Kaunseling dan Pengurusan Kredit
              </a>{" "}
              is a Bank Negara-backed service that helps restructure loans and
              credit card debt. It's completely free, and one of the most
              underused resources in Malaysia.
            </p>
          </div>
        </section>

        {/*HIREDLY*/}
        <section id="hiredly" className="hiredly-section">
          <div className="wrap">
            <span className="eyebrow">Step 3 — Get hired</span>
            <h2>Fast-Track Your Job Search: Three Ways Hiredly Can Help</h2>
            <p>
              Job hunting after a layoff is hard enough. Let us help you find
              your next role.
            </p>

            <div class="pillars">
              <div class="pillar">
                <div class="pillar-label">Pillar 01</div>
                <h3>Get seen by employers first</h3>
                <p>
                  Create your{" "}
                  <a
                    href="https://my.hiredly.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Hiredly
                  </a>{" "}
                  profile today to discover career opportunities tailored to
                  your expertise. Take the next step and kickstart your job
                  search seamlessly with us.
                </p>
              </div>

              <div class="pillar">
                <div class="pillar-label">Pillar 02</div>
                <h3>A CV that gets past filters, built in minutes</h3>
                <p>
                  Need a resume refresh?{" "}
                  <a
                    href="https://www.hiresume.my/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HiResume.my
                  </a>
                  , Hiredly’s dedicated AI CV builder, creates a recruiter-ready
                  CV formatted for the ATS systems Malaysian employers actually
                  use. We use our 11 years of local data to help you craft a
                  resume that lands interviews, so you can be applying within
                  the hour.
                </p>
              </div>

              <div class="pillar">
                <div class="pillar-label">Pillar 03</div>
                <h3>Handpicked roles based on your new resume</h3>
                <p>
                  Once your profile and CV are ready, our smart recommendation
                  system on{" "}
                  <a
                    href="https://my.hiredly.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Hiredly
                  </a>{" "}
                  takes over. You will receive personalised job matches based on
                  your resume, paving the way for a seamless, hassle-free
                  connection with actively hiring companies.
                </p>
              </div>
            </div>

            <div className="hiredly-cta" id="signup">
              <a
                href="https://my.hiredly.com/?signup=true"
                className="btn btn-coral"
                target="_blank"
                rel="noopener noreferrer"
              >
                Create my Hiredly profile →
              </a>
              <span className="btn-note" style={{ color: "var(--ink)" }}>
                Takes 3 minutes. Get priority visibility.
              </span>
              <div className="trust-bar">
                <span className="trust-stat">
                  <span className="trust-value">42,000+</span>
                  <span className="trust-label">Registered employers</span>
                </span>
                <span className="trust-stat">
                  <span className="trust-value">Verified</span>
                  <span className="trust-label">Employers only</span>
                </span>
                <span className="trust-stat">
                  <span className="trust-value">9M+</span>
                  <span className="trust-label">
                    Social reach every quarter
                  </span>
                </span>
              </div>
              <div className="trust-bar trust-bar--secondary">
                <span className="trust-stat">
                  <span className="trust-value">100,000+</span>
                  <span className="trust-label">Monthly active users</span>
                </span>
                <span className="trust-stat">
                  <span className="trust-value">1.3M+</span>
                  <span className="trust-label">Talent database</span>
                </span>
                <span className="trust-stat">
                  <span className="trust-value">10,000+</span>
                  <span className="trust-label">Active jobs monthly</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/*RESOURCES & PARTNERS*/}
        <section id="partners" className="partners-section">
          <div className="wrap">
            <span className="eyebrow">Resources & partners</span>
            <h2>Services worth knowing about</h2>

            <div className="partner-note">
              <strong>Transparency note:</strong> We may earn a small referral
              fee from a few of the partners below, but we only highlight
              services we genuinely believe in. Everything else in this guide is
              completely free advice, with no strings attached.
            </div>

            {/*Bridge income*/}
            <div id="bridge-income" className="partner-group">
              <h3
                dangerouslySetInnerHTML={{
                  __html: pageContent?.partnerListTopSection?.sectionTitle,
                }}
              ></h3>
              <p
                className="group-intro"
                dangerouslySetInnerHTML={{
                  __html:
                    pageContent?.partnerListTopSection?.sectionDescription,
                }}
              ></p>

              {pageContent?.partnerListTop?.map((partner) => (
                <PartnerFlipCard key={partner.id} partner={partner} />
              ))}
            </div>

            {/*Health insurance*/}
            <div id="health-insurance" className="partner-group">
              <h3
                dangerouslySetInnerHTML={{
                  __html: pageContent?.partnerListMiddleSection?.sectionTitle,
                }}
              ></h3>
              <p
                className="group-intro"
                dangerouslySetInnerHTML={{
                  __html:
                    pageContent?.partnerListMiddleSection?.sectionDescription,
                }}
              ></p>

              {pageContent?.resourcesLinkCards?.map((card) => (
                <ResourceLinkCard key={card.id} card={card} />
              ))}
            </div>

            {/*Mental health*/}
            <div id="mental" className="partner-group">
              <h3
                dangerouslySetInnerHTML={{
                  __html: pageContent?.partnerListBottomSection?.sectionTitle,
                }}
              ></h3>
              <p
                className="group-intro"
                dangerouslySetInnerHTML={{
                  __html:
                    pageContent?.partnerListBottomSection?.sectionDescription,
                }}
              ></p>

              {pageContent?.partnerListBottom?.map((partner) => (
                <PartnerFlipCard key={partner.id} partner={partner} />
              ))}
            </div>
          </div>
        </section>

        {/*UPSKILL ADVICE*/}
        <section id="upskill">
          <div className="wrap">
            <span
              className="eyebrow"
              dangerouslySetInnerHTML={{
                __html: pageContent?.upskillSection?.tagline,
              }}
            ></span>
            <h2
              dangerouslySetInnerHTML={{
                __html: pageContent?.upskillSection?.sectionTitle,
              }}
            ></h2>
            <p
              className="lead"
              dangerouslySetInnerHTML={{
                __html: pageContent?.upskillSection?.sectionDescription,
              }}
            ></p>
            <h3
              style={{ marginTop: "1.5rem" }}
              dangerouslySetInnerHTML={{
                __html: pageContent?.upskillSection?.sectionSubtitle,
              }}
            ></h3>
            <p
              style={{ marginBottom: "2rem" }}
              dangerouslySetInnerHTML={{
                __html: pageContent?.upskillSection?.sectionSubDescription,
              }}
            ></p>
            {pageContent?.upskillSection?.upskillLinkCards?.map((card) => (
              <ResourceLinkCard key={card.id} card={card} />
            ))}
          </div>
        </section>

        {/*SCAMS*/}
        <section id="scams">
          <div className="wrap">
            <span className="eyebrow">Stay safe</span>
            <h2>Watch out for scams targeting job seekers</h2>
            <p className="lead">
              Laid-off Malaysians are a prime target. A few patterns to
              recognise immediately.
            </p>

            <div className="scam-list">
              <div className="scam-item">
                <div className="scam-flag">⚑</div>
                <p>
                  <strong>WhatsApp "part-time RM300/day" offers.</strong>{" "}
                  Usually MLM recruitment or money-laundering mule schemes.
                  Legitimate employers don't cold-message you on WhatsApp.
                </p>
              </div>
              <div className="scam-item">
                <div className="scam-flag">⚑</div>
                <p>
                  <strong>"Training fees" or "equipment deposits."</strong> No
                  legitimate employer asks you to pay to start a job. Walk away.
                </p>
              </div>
              <div className="scam-item">
                <div className="scam-flag">⚑</div>
                <p>
                  <strong>
                    Fake recruiters asking for bank details upfront.
                  </strong>{" "}
                  Banking info is for payroll after you're hired — not for
                  "verification" during interviews.
                </p>
              </div>
              <div className="scam-item">
                <div className="scam-flag">⚑</div>
                <p>
                  <strong>Telegram groups promising daily high income.</strong>{" "}
                  If it sounds too good to be true, it is.
                </p>
              </div>
            </div>

            <h3 style={{ marginTop: "1.5rem", marginBottom: "0.5rem" }}>
              Not sure if it's really us?
            </h3>
            <p style={{ marginTop: "0" }}>
              Every job on{" "}
              <a href="https://hiredly.com" className="content-link">
                Hiredly
              </a>{" "}
              is verified from a registered Malaysian employer.
              <br />
              If you receive a suspicious message claiming to be from Hiredly,
              reach out to our team directly to verify:
              <br />
              <br />
              <a
                href="mailto:hello@hiredly.com"
                className="content-link"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.45rem",
                  marginBottom: "0.75rem",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    width: "1em",
                    height: "1em",
                    color: "#4a4a4a",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <path d="M3.75 5.25h16.5A2.25 2.25 0 0 1 22.5 7.5v9a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 16.5v-9a2.25 2.25 0 0 1 2.25-2.25Zm15.03 1.5H5.22L12 11.61l6.78-4.86ZM3 15.98V7.57l8.13 5.82a1.5 1.5 0 0 0 1.74 0L21 7.57v8.41a.75.75 0 0 1-.75.75H3.75a.75.75 0 0 1-.75-.75Z"></path>
                  </svg>
                </span>
                <span>hello@hiredly.com</span>
              </a>
              <br />
              <a
                href="tel:+60327710141"
                className="content-link"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.45rem",
                  marginBottom: "0.75rem",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    width: "1em",
                    height: "1em",
                    color: "#4a4a4a",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <path d="M6.62 10.79a15.531 15.531 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.11-.21c1.2.48 2.5.74 3.88.74a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.61 21 3 13.39 3 4a1 1 0 0 1 1-1h3.29a1 1 0 0 1 1 1c0 1.38.26 2.68.74 3.88a1 1 0 0 1-.21 1.11l-2.2 2.2Z"></path>
                  </svg>
                </span>
                <span>+603-2771 0141</span>
              </a>{" "}
              (Office)
              <br />
              <a
                href="https://wa.me/601154460461"
                className="content-link"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.45rem",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    width: "1em",
                    height: "1em",
                    color: "#4a4a4a",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <path d="M.5 23.5 2.1 18A11.5 11.5 0 0 1 0 11.5C0 5.2 5.2 0 11.5 0S23 5.2 23 11.5 17.8 23 11.5 23c-2 .0-3.9-.5-5.6-1.4L.5 23.5Zm6.2-3.6.4.2c1.5.9 3.1 1.4 4.9 1.4 5.3 0 9.6-4.3 9.6-9.6S17.3 2.3 12 2.3 2.4 6.6 2.4 11.9c0 1.8.5 3.4 1.4 4.9l.2.4-1 3.5 3.6-1.1Zm10.6-4.8c-.1.3-.7.7-1.2.8-.3.1-.7.1-1.1 0-.6-.1-1.4-.3-2.3-.7-2-.9-3.3-2.5-3.4-2.6-.1-.1-1-1.3-1-2.5 0-1.2.6-1.8.8-2 .2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.8 1.9c.1.2.1.4 0 .6-.1.2-.2.4-.4.6-.1.1-.3.3-.2.5.1.2.5.8 1.1 1.3.8.7 1.4.9 1.6 1 .2.1.4.1.5 0 .2-.2.8-.9 1-1.2.2-.2.4-.2.6-.1l1.8.9c.2.1.3.2.4.3.1.2.1.6 0 .9Z"></path>
                  </svg>
                </span>
                <span>+6011-5446 0461</span>
              </a>{" "}
              (Customer Service)
            </p>

            <div className="scam-proof">
              <p className="scam-proof-intro">
                <strong>
                  Examples below show how scammers can impersonate Hiredly.
                </strong>{" "}
                If the message asks for personal details and payments, treat it
                as a red flag 🚩
              </p>
              <div className="scam-proof-grid">
                <figure className="scam-proof-card">
                  <img
                    src={landingAsset("hiredly-scam-1.png")}
                    alt="Screenshot of a Telegram scam impersonating Hiredly"
                  />
                </figure>
                <figure className="scam-proof-card">
                  <img
                    src={landingAsset("hiredly-scam-2-new.png")}
                    alt="Screenshot of a scammer impersonating Hiredly on mobile"
                  />
                </figure>
                <figure className="scam-proof-card">
                  <img
                    src={landingAsset("hiredly-scam-3.png")}
                    alt="Screenshot of a WhatsApp scam impersonating Hiredly"
                  />
                </figure>
              </div>
              <p style={{ marginTop: "0.75rem" }}>
                You can also check our Instagram highlights for reported
                scammers and impersonation examples:{" "}
                <a
                  href="https://www.instagram.com/s/aGlnaGxpZ2h0OjE4MTA5NzA4NTI1ODcyMDQ3?igsh=dzZrczVic2hoZG41"
                  className="content-link"
                >
                  Instagram highlights
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        {/*INTERVIEW PREP*/}
        <section id="interview">
          <div className="wrap">
            <span className="eyebrow">When the interviews come</span>
            <h2>Interview & negotiation prep</h2>
            <p className="lead">
              How you talk about the layoff matters more than whether it
              happened.
            </p>

            <h3 style={{ marginTop: "1.5rem" }}>
              Explaining a layoff without apologising for it
            </h3>
            <p>
              Keep it brief, factual, and forward-looking. "The company
              restructured and my role was part of the reduction. I'm focused on
              finding a team where I can contribute [specific skill or
              outcome]." No blame, no over-explanation, no shame. Interviewers
              have seen hundreds of retrenchments — it's not a mark against you.
            </p>

            <h3 style={{ marginTop: "1.5rem" }}>
              Know your worth before you negotiate
            </h3>
            <p>
              Don't guess your market rate. Our annual{" "}
              <a
                href="https://my.hiredly.com/employer/market-insights/salary-guide-2026"
                className="content-link"
              >
                Hiredly Salary Report
              </a>{" "}
              benchmarks roles across Malaysian industries — useful whether
              you're interviewing now or planning ahead.
            </p>

            <div className="reading-block">
              <span className="eyebrow">Related reading</span>
              <h3 style={{ marginTop: "0" }}>More on salary and negotiating</h3>
              <RelatedReadingGrid articles={pageContent?.articles} />
            </div>

            <div className="video-note">
              <p>
                We’ve curated some of our best video guides right here to help
                you nail your next interview and negotiate the salary you
                deserve. You can watch them directly on this page, or head over
                to{" "}
                <a
                  href="https://www.tiktok.com/@hellomentorwithderektoh"
                  className="content-link"
                >
                  @hellomentorwithderektoh
                </a>{" "}
                on TikTok or our{" "}
                <a
                  href="https://www.youtube.com/playlist?list=PLHLcF_MeUZeZ7H455aJ_WMb5nstYBJBxH"
                  className="content-link"
                >
                  YouTube playlist
                </a>{" "}
                for more career insights.
              </p>
            </div>

            <InterviewVideoGrid videoLinks={pageContent?.videoLinks} />
          </div>
        </section>

        {/*FAQ*/}
        <section id="faq">
          <div className="wrap">
            <span className="eyebrow">Questions & answers</span>
            <h2>FAQ</h2>

            <div className="faq-item">
              <button className="faq-question">
                How long does EIS take to pay out?{" "}
                <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  Typically 2–4 weeks from a complete application. Incomplete
                  documents are the most common cause of delay — double-check
                  your termination letter clearly states the reason before
                  submitting.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question">
                Can I claim EIS if I took VSS (Voluntary Separation Scheme)?{" "}
                <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  <strong>Yes, you can.</strong> Whether it’s Retrenchment, VSS,
                  or MSS, these are all recognized as a loss of employment. If
                  your contract ended under any of these schemes, you’re
                  eligible for EIS benefits. We recommend a quick check with
                  PERKESO to confirm the specifics of your case.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question">
                Should I take any job, or wait for the right one?{" "}
                <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  Depends on your runway. With EIS covering up to 6 months, most
                  people can afford to be selective for the first 2–3 months. If
                  you're approaching month 4 without offers, widen the search —
                  an interim role that pays the bills is better than burning
                  savings.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question">
                Will a gap on my CV hurt me? <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  Short gaps (under 6 months) barely register with Malaysian
                  employers, especially in the current climate. Anything longer
                  is worth addressing briefly in your cover letter — upskilling
                  courses, freelance work, or caregiving responsibilities all
                  count as legitimate reasons.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question">
                Can foreigners working in Malaysia claim EIS?{" "}
                <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  No. EIS is currently only available to Malaysian citizens and
                  permanent residents who have contributed to the scheme.
                  Foreign workers who've lost their jobs should speak to their
                  employer about final entitlements and, if holding a work pass,
                  their pass status.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <button className="faq-question">
                Is my EIS payout taxable? <span className="faq-icon">+</span>
              </button>
              <div className="faq-answer">
                <p>
                  EIS benefits are generally not subject to income tax.
                  Retrenchment benefits from your employer may have partial tax
                  exemptions depending on years of service — check with LHDN or
                  a tax adviser for your specific situation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/*FINAL CTA*/}
        <section className="final-cta">
          <div className="wrap">
            <h2>
              You didn't choose this. You get to choose what happens next.
            </h2>
            <p>
              Start with one step — create your{" "}
              <a href="https://hiredly.com" className="content-link">
                Hiredly
              </a>{" "}
              profile.
            </p>
            <div className="cta-row">
              <a
                href="https://my.hiredly.com/?signup=true"
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Create my Hiredly profile →
              </a>
            </div>
          </div>
        </section>

        {/*FOOTER*/}
        <footer>
          <div className="wrap">
            <div className="brand-foot" aria-label="Hiredly">
              <img
                src={`/images/hiredly-logo.svg`}
                alt="Hiredly"
                className="brand-foot-logo"
              />
            </div>
            <p>Helping Malaysians find their next role faster.</p>
            <p style={{ marginTop: "1rem" }}>
              <a href="https://my.hiredly.com/about-us">About</a>
              {" · "}
              <a href="https://my.hiredly.com/privacy-policy">Privacy</a>
              {" · "}
              <a href="https://my.hiredly.com/terms-and-conditions">Terms</a>
              {" · "}
              <a href="https://my.hiredly.com/contact-us">Contact</a>
            </p>
          </div>
        </footer>
      </div>
      <SharedSnackbar />
    </Fragment>
  );
}

export default HiredlyRetrenchmentSupportGuide;
